// src/components/LearnerLoginCard.tsx
//
// Shown to a parent on a child's Learn page. Children 9+ can sign in by
// themselves with a username + password the parent chooses (no email).
// The parent can change the password or username, or turn the login off;
// any change ends a sign-in already open on the child's device.

import { useState } from "react";
import {
  errorText,
  useGetLearnerLoginsQuery,
  useRemoveLearnerLoginMutation,
  useSaveLearnerLoginMutation,
} from "../app/api/learningApi";

const fmt = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString("en-NG", {
        day: "numeric",
        month: "short",
        hour: "numeric",
        minute: "2-digit",
      })
    : null;

const inputCls =
  "w-full rounded-xl border border-[#dde2ec] bg-white px-3 py-2 text-sm text-[#16213e] outline-none focus:border-[#1b2d5e]";

export default function LearnerLoginCard({ childId }: { childId: string }) {
  const { data } = useGetLearnerLoginsQuery();
  const [save, { isLoading: saving }] = useSaveLearnerLoginMutation();
  const [remove, { isLoading: removing }] = useRemoveLearnerLoginMutation();

  const row = data?.data.find((r) => r.childId === childId);
  const [mode, setMode] = useState<"idle" | "create" | "password" | "username">("idle");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  if (!row?.eligible) return null;
  const { firstName, login } = row;

  const open = (m: typeof mode) => {
    setMode(m);
    setNote(null);
    setPassword("");
    setShow(m === "create" || m === "password");
    setUsername(m === "username" ? login?.username ?? "" : row.suggestion ?? "");
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNote(null);
    const body: { childId: string; username?: string; password?: string } = { childId };
    if (mode === "create" || mode === "username") body.username = username.trim();
    if (mode === "create" || mode === "password") body.password = password;
    try {
      const res = await save(body).unwrap();
      setNote({
        ok: true,
        text:
          mode === "create"
            ? `Done. ${firstName} signs in at futuresmartlab.com/login with the username ${res.data.login?.username} and the password you chose.`
            : res.message,
      });
      setMode("idle");
      setPassword("");
    } catch (err) {
      setNote({ ok: false, text: errorText(err, "Could not save the login") });
    }
  };

  const turnOff = async () => {
    if (!window.confirm(`Turn off ${firstName}'s login? They will be signed out straight away.`)) return;
    setNote(null);
    try {
      const res = await remove(childId).unwrap();
      setNote({ ok: true, text: res.message ?? "Login turned off." });
      setMode("idle");
    } catch (err) {
      setNote({ ok: false, text: errorText(err, "Could not turn the login off") });
    }
  };

  return (
    <div className="rounded-[24px] border border-[#e5e8ef] bg-white p-5">
      <p className="text-sm font-bold text-[#16213e]">
        {login ? `${firstName}'s own sign-in` : `Let ${firstName} sign in on their own`}
      </p>

      {login ? (
        <p className="mt-1 text-sm leading-6 text-[#5f6780]">
          Username <span className="font-mono font-semibold text-[#16213e]">{login.username}</span>
          {" · "}
          {login.lastLoginAt ? `last signed in ${fmt(login.lastLoginAt)}` : "not signed in yet"}.
          {" "}They see only their lessons; you still see everything here.
        </p>
      ) : (
        <p className="mt-1 text-sm leading-6 text-[#5f6780]">
          Choose a username and password for {firstName}. No email needed. They will see only their
          own lessons, and you still see everything here.
        </p>
      )}

      {mode === "idle" && (
        <div className="mt-3 flex flex-wrap gap-2">
          {login ? (
            <>
              <button type="button" onClick={() => open("password")} className="rounded-xl border border-[#dde2ec] px-3 py-1.5 text-xs font-semibold text-[#3b4560] hover:bg-[#f1f3f8]">
                Change password
              </button>
              <button type="button" onClick={() => open("username")} className="rounded-xl border border-[#dde2ec] px-3 py-1.5 text-xs font-semibold text-[#3b4560] hover:bg-[#f1f3f8]">
                Change username
              </button>
              <button type="button" onClick={turnOff} disabled={removing} className="rounded-xl px-3 py-1.5 text-xs font-semibold text-[#b94a3a] hover:bg-[#fff5f3] disabled:opacity-50">
                Turn off
              </button>
            </>
          ) : (
            <button type="button" onClick={() => open("create")} className="rounded-xl bg-[#1b2d5e] px-4 py-2 text-xs font-semibold text-white hover:bg-[#24397a]">
              Set up {firstName}'s login
            </button>
          )}
        </div>
      )}

      {mode !== "idle" && (
        <form onSubmit={submit} className="mt-3 space-y-3">
          {(mode === "create" || mode === "username") && (
            <label className="block text-xs font-semibold text-[#3b4560]">
              Username
              <input
                className={`${inputCls} mt-1 font-mono`}
                value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase())}
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                maxLength={24}
                required
              />
              <span className="mt-1 block font-normal text-[12.5px] text-[#8189a0]">
                First name only. No surname, school or birthday.
              </span>
            </label>
          )}
          {(mode === "create" || mode === "password") && (
            <label className="block text-xs font-semibold text-[#3b4560]">
              {mode === "create" ? "Password" : "New password"}
              <div className="mt-1 flex gap-2">
                <input
                  className={inputCls}
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  minLength={6}
                  maxLength={64}
                  required
                />
                <button type="button" onClick={() => setShow((v) => !v)} className="rounded-xl border border-[#dde2ec] px-3 text-xs font-semibold text-[#3b4560]">
                  {show ? "Hide" : "Show"}
                </button>
              </div>
              <span className="mt-1 block font-normal text-[12.5px] text-[#8189a0]">
                At least 6 characters. Something {firstName} can remember but others can't guess.
              </span>
            </label>
          )}
          {mode !== "create" && (
            <p className="text-[12.5px] text-[#b26a1d]">Saving signs {firstName} out of any device they are using.</p>
          )}
          <div className="flex gap-2">
            <button type="submit" disabled={saving} className="rounded-xl bg-[#1b2d5e] px-4 py-2 text-xs font-semibold text-white hover:bg-[#24397a] disabled:opacity-50">
              {saving ? "Saving…" : mode === "create" ? "Create login" : "Save"}
            </button>
            <button type="button" onClick={() => setMode("idle")} className="rounded-xl px-3 py-2 text-xs font-semibold text-[#5f6780] hover:bg-[#f1f3f8]">
              Cancel
            </button>
          </div>
        </form>
      )}

      {note && (
        <p className={`mt-3 text-xs font-semibold ${note.ok ? "text-[#2f7a4f]" : "text-[#b94a3a]"}`}>{note.text}</p>
      )}
    </div>
  );
}
